import type { Directive, DirectiveBinding, ComponentPublicInstance, Ref } from 'vue'
import { isRef, unref } from 'vue'
import { useUserStore } from '@/store/modules/user'
import { MenuType } from '@/enum/global-enums'

type TabItem = {
  name: string
  label: string
  closable?: boolean
  [key: string]: string | boolean | undefined
}

type BindingValue = {
  action: string
  tabName: string
  tabsRef: Ref<TabItem[]> | TabItem[]
  activeRef?: Ref<string> | string
  fallbackTab?: string
}

type RouteLike = {
  meta?: {
    actions?: string[]
  }
}

interface NewEl extends HTMLElement {
  __v_tab_permission_original_tabs__?: TabItem[]
}

const ORIGINAL_TABS_KEY = '__v_tab_permission_original_tabs__'

// 处理特殊字符分割
function normalizeAction(action: string): string {
  const value = String(action || '').trim()
  if (!value) return ''

  if (value.includes('@')) return value

  const parts = value.split('.').filter(Boolean)

  if (parts.length === 3) return `${parts[1]}@${parts[2]}`
  if (parts.length === 2) return `${parts[0]}@${parts[1]}`

  return value
}

// 获取路由中的permission权限表
function getRouteActions(binding: DirectiveBinding<BindingValue>): string[] {
  const instance = binding.instance as ComponentPublicInstance & { $route?: RouteLike }
  const actions = instance?.$route?.meta?.actions
  if (!Array.isArray(actions)) return []
  return actions.map((item) => normalizeAction(item)).filter(Boolean)
}

// 获取按钮权限
function getUserButtonActions(): string[] {
  const userStore = useUserStore()

  return (userStore.getPermissions || [])
    .filter((permission) => permission.type === MenuType.BUTTON_TYPE && permission.permission_mark)
    .map((permission) => normalizeAction(permission.permission_mark))
    .filter(Boolean)
}

// 权限判定
function hasAction(binding: DirectiveBinding<BindingValue>, action: string): boolean {
  const mark = normalizeAction(action)
  if (!mark) return false

  const routeActions = getRouteActions(binding)
  const availableActions = routeActions.length ? routeActions : getUserButtonActions()

  return availableActions.includes(mark)
}

function getRefValue<T>(target: Ref<T> | T): T {
  return unref(target)
}

function setTabsValue(target: Ref<TabItem[]> | TabItem[] | undefined, value: TabItem[]) {
  if (!target) return

  if (isRef(target)) {
    target.value = value
    return
  }

  if (Array.isArray(target)) {
    target.splice(0, target.length, ...value)
  }
}

function setActiveValue(target: Ref<string> | string | undefined, value: string) {
  if (!target) return

  if (isRef(target)) {
    target.value = value
  }
}

// 新旧tab进行判重
function isSameTabs(a: TabItem[], b: TabItem[]) {
  if (a.length !== b.length) return false

  return a.every((item, index) => {
    const target = b[index]
    return item.name === target?.name && item.label === target?.label && item.closable === target?.closable
  })
}

// 更新tabs，真正处理tab的值
function updateTabs(el: HTMLElement, binding: DirectiveBinding<BindingValue>) {
  const config = binding.value

  if (!config || !config.action || !config.tabName || !config.tabsRef) {
    throw new Error('v-tab-permission 需要传入 { action, tabName, tabsRef, activeRef?, fallbackTab? }')
  }

  const currentTabs = getRefValue(config.tabsRef) || []
  const targetEl = el as NewEl

  if (!targetEl[ORIGINAL_TABS_KEY]) {
    targetEl[ORIGINAL_TABS_KEY] = [...currentTabs]
  }

  const originalTabs = [...(targetEl[ORIGINAL_TABS_KEY] || [])]
  const allow = hasAction(binding, config.action)

  const nextTabs = allow ? [...originalTabs] : originalTabs.filter((tab) => tab.name !== config.tabName)

  // 只有 tabs 真变了才更新
  if (!isSameTabs(currentTabs, nextTabs)) {
    setTabsValue(config.tabsRef, nextTabs)
  }

  const activeValue = isRef(config.activeRef) ? config.activeRef.value : config.activeRef
  const exists = nextTabs.some((tab) => tab.name === activeValue)

  if (!exists) {
    const fallback = config.fallbackTab || (nextTabs[0]?.name ?? '')
    if (fallback && fallback !== activeValue) {
      setActiveValue(config.activeRef, fallback)
    }
  }
}

// 更新与加载时对tab进行维护
const tabPermissionDirective: Directive<HTMLElement, BindingValue> = {
  mounted(el, binding) {
    updateTabs(el, binding)
  },
  updated(el, binding) {
    updateTabs(el, binding)
  }
}

export default tabPermissionDirective
